import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import {
  fetchAceApplicationsData,
  fetchActiveApplicationDetails,
  fetchApplicationsData,
  fetchApplicationsForDashboard
} from 'features/applications/applicationsThunks';

import {
  Application,
  ApplicationsList,
  ApplicationsState
} from 'types/applications';

import { RequestStatus } from 'constants/enums';
import { mapApplicationsFacetsForStore } from 'constants/facets';

export const emptyApplicationsFacets = {
  education: {},
  languages: {},
  countries: {},
  skillsets: {},
  clientStage: {},
  stage: {}
};

const initialState: ApplicationsState = {
  applications: [],
  facets: {
    ...emptyApplicationsFacets
  },
  status: RequestStatus.Idle,
  error: null,
  count: 0,
  listMeta: null,
  dashboardApplications: [],
  isDashboardApplicationsLoading: false,
  activeApplicationDetails: null
};

const applicationsSlice = createSlice({
  name: 'applications',
  initialState,
  reducers: {
    updateApplication: (state, action: PayloadAction<Partial<Application>>) => {
      const { id, ...changes } = action.payload;
      state.applications = state.applications.map((application) =>
        application.id === id ? { ...application, ...changes } : application
      );
      state.dashboardApplications = state.dashboardApplications.map(
        (application) =>
          application.id === id ? { ...application, ...changes } : application
      );

      if (id === state.activeApplicationDetails?.id) {
        state.activeApplicationDetails = {
          ...state.activeApplicationDetails,
          ...changes
        } as Application;
      }
    },
    dropApplications: (state) => {
      state.applications = [];
      state.facets = { ...emptyApplicationsFacets };
      state.count = 0;
      state.listMeta = null;
    },
    dropActiveApplicationDetails: (state) => {
      state.activeApplicationDetails = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchApplicationsData.pending, (state) => {
        state.status = RequestStatus.Loading;
      })
      .addCase(
        fetchApplicationsData.fulfilled,
        (state, action: PayloadAction<ApplicationsList>) => {
          state.status = RequestStatus.Succeeded;
          state.applications = action.payload.content;
          const newFacets = mapApplicationsFacetsForStore(
            action?.payload?.facets
          );
          state.facets = newFacets;
          state.count = action.payload.totalElements;
          state.error = null;
          state.listMeta = {
            empty: action.payload.empty,
            first: action.payload.first,
            last: action.payload.last,
            pageable: action.payload.pageable,
            totalElements: action.payload.totalElements,
            totalPages: action.payload.totalPages,
            number: action.payload.number,
            numberOfElements: action.payload.numberOfElements,
            size: action.payload.size
          };
        }
      )
      .addCase(fetchApplicationsData.rejected, (state, action) => {
        state.status = RequestStatus.Failed;
        state.error = action.error.message || null;
      });
    builder
      .addCase(fetchAceApplicationsData.pending, (state) => {
        state.status = RequestStatus.Loading;
      })
      .addCase(
        fetchAceApplicationsData.fulfilled,
        (state, action: PayloadAction<ApplicationsList>) => {
          state.status = RequestStatus.Succeeded;
          state.applications = action.payload.content;
          state.count = action.payload.totalElements;
          state.error = null;
        }
      )
      .addCase(fetchAceApplicationsData.rejected, (state, action) => {
        state.status = RequestStatus.Failed;
        state.error = action.error.message || null;
      })

      .addCase(fetchApplicationsForDashboard.fulfilled, (state, action) => {
        state.dashboardApplications = action.payload.content;
        state.isDashboardApplicationsLoading = false;
      })
      .addCase(fetchApplicationsForDashboard.pending, (state) => {
        state.isDashboardApplicationsLoading = true;
      })
      .addCase(fetchApplicationsForDashboard.rejected, (state) => {
        state.dashboardApplications = [];
        state.isDashboardApplicationsLoading = false;
      })
      .addCase(
        fetchActiveApplicationDetails.fulfilled,
        (state, action: PayloadAction<Application>) => {
          state.activeApplicationDetails = action.payload;
          const index = state.applications.findIndex(
            (application) => application.id === action.payload.id
          );
          if (index !== -1) {
            state.applications[index] = {
              ...state.applications[index],
              ...action.payload
            };
          }
        }
      )
      .addCase(fetchActiveApplicationDetails.rejected, (state) => {
        state.activeApplicationDetails = null;
      });
  }
});

export const {
  updateApplication,
  dropApplications,
  dropActiveApplicationDetails
} = applicationsSlice.actions;

export default applicationsSlice.reducer;
